import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Paperclip, Link2, CalendarClock } from "lucide-react";
import { Task, TaskStatus } from "../../types/tasks";
import { TASK_TYPE_ICON } from "../../constants/taskConstants";

interface Props {
  tasks: Task[];
  onEdit: (task: Task) => void;
  onStatusChange: (taskId: string, status: TaskStatus) => void;
}

// Done is intentionally last so it feels like a "completed" lane
const COLUMNS: { status: TaskStatus; labelKey: string }[] = [
  { status: "new",         labelKey: "form.statusOptions.new" },
  { status: "in_progress", labelKey: "form.statusOptions.in_progress" },
  { status: "overdue",     labelKey: "form.statusOptions.overdue" },
  { status: "done",        labelKey: "form.statusOptions.done" },
];

const COLUMN_STYLES: Record<TaskStatus, { header: string; dot: string; bg: string }> = {
  new:         { header: "border-gray-300",  dot: "bg-gray-400",   bg: "bg-gray-50/50" },
  in_progress: { header: "border-blue-200",  dot: "bg-blue-500",   bg: "bg-blue-50/30" },
  overdue:     { header: "border-red-200",   dot: "bg-red-500",    bg: "bg-red-50/30" },
  done:        { header: "border-green-200", dot: "bg-green-500",  bg: "bg-green-50/30" },
};

const DRAG_KEY = "task-kanban-id";

const formatNextDue = (task: Task): { label: string; tone: "default" | "today" | "overdue" } | null => {
  const upcoming = task.activities
    .filter((a) => a.dueDate && !a.completedAt)
    .sort(
      (a, b) =>
        new Date(`${a.dueDate}T${a.dueTime ?? "00:00"}`).getTime() -
        new Date(`${b.dueDate}T${b.dueTime ?? "00:00"}`).getTime(),
    )[0];
  if (!upcoming?.dueDate) return null;
  const due     = new Date(upcoming.dueDate);
  const today   = new Date(); today.setHours(0, 0, 0, 0);
  const dueDay  = new Date(due); dueDay.setHours(0, 0, 0, 0);
  if (dueDay < today)  return { label: upcoming.dueDate, tone: "overdue" };
  if (dueDay.getTime() === today.getTime()) return { label: "Today", tone: "today" };
  return { label: due.toLocaleDateString(undefined, { month: "short", day: "numeric" }), tone: "default" };
};

interface CardProps {
  task: Task;
  onEdit: (t: Task) => void;
}

const KanbanCard: React.FC<CardProps> = ({ task, onEdit }) => {
  const TypeIcon = TASK_TYPE_ICON[task.type];
  const due = formatNextDue(task);
  const isDone = task.status === "done";

  return (
    <div
      draggable={!isDone}
      onDragStart={(e) => {
        if (isDone) { e.preventDefault(); return; }
        e.dataTransfer.setData(DRAG_KEY, task._id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={() => onEdit(task)}
      className={`bg-white rounded-lg border border-gray-200 shadow-sm p-3 select-none group transition-all ${
        isDone
          ? "opacity-70 cursor-pointer"
          : "cursor-grab hover:shadow-md hover:border-gray-300 active:cursor-grabbing"
      }`}
    >
      {/* Header row */}
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <TypeIcon className="w-3.5 h-3.5 text-gray-400 shrink-0" />
          <span className="text-[11px] text-gray-400 font-mono">{task.taskNumber}</span>
          {task.useStructuredForm && (
            <Paperclip className="w-3 h-3 text-gray-300 shrink-0" />
          )}
          {task.linkedShipmentId && (
            <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-medium bg-indigo-50 text-indigo-600 border border-indigo-200">
              <Link2 className="w-2 h-2" />
              {task.linkedShipmentNumber ?? "Shipment"}
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      <p className="text-[13px] font-medium text-gray-800 line-clamp-2 leading-snug mb-2">
        {task.description}
      </p>

      {/* Client */}
      {task.clientName && (
        <p className="text-[11px] text-gray-500 mb-2 truncate">{task.clientName}</p>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 mt-1">
        <span className="text-[11px] text-gray-400 truncate">{task.assigneeName}</span>
        {due && (
          <div className={`flex items-center gap-1 text-[11px] font-medium shrink-0 ${
            due.tone === "overdue" ? "text-red-600" : due.tone === "today" ? "text-blue-600" : "text-gray-400"
          }`}>
            <CalendarClock className="w-3 h-3" />
            {due.label}
          </div>
        )}
      </div>
    </div>
  );
};

interface ColumnProps {
  status: TaskStatus;
  labelKey: string;
  tasks: Task[];
  onEdit: (t: Task) => void;
  onDrop: (e: React.DragEvent, targetStatus: TaskStatus) => void;
}

const KanbanColumn: React.FC<ColumnProps> = ({ status, labelKey, tasks, onEdit, onDrop }) => {
  const { t } = useTranslation("tasks");
  const [isDragOver, setIsDragOver] = useState(false);
  const styles = COLUMN_STYLES[status];
  const isDoneCol = status === "done";

  return (
    <div
      className={`flex flex-col rounded-xl border ${styles.header} overflow-hidden flex-1 min-w-[200px]`}
      onDragOver={(e) => {
        if (isDoneCol) return; // Can't drop into Done
        e.preventDefault();
        setIsDragOver(true);
      }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        setIsDragOver(false);
        if (!isDoneCol) onDrop(e, status);
      }}
    >
      {/* Column header */}
      <div className={`flex items-center gap-2 px-3 py-2.5 border-b ${styles.header} bg-white`}>
        <span className={`w-2 h-2 rounded-full ${styles.dot}`} />
        <span className="text-[12px] font-semibold text-gray-700 uppercase tracking-wider">
          {t(labelKey)}
        </span>
        <span className="ml-auto text-[11px] font-medium text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded-full">
          {tasks.length}
        </span>
      </div>

      {/* Cards */}
      <div
        className={`flex-1 p-2 space-y-2 overflow-y-auto transition-colors ${
          isDragOver ? "bg-blue-50/70 ring-2 ring-inset ring-blue-300" : styles.bg
        }`}
        style={{ minHeight: "120px", maxHeight: "calc(100vh - 280px)" }}
      >
        {tasks.map((task) => (
          <KanbanCard key={task._id} task={task} onEdit={onEdit} />
        ))}
        {tasks.length === 0 && (
          <div className={`flex items-center justify-center h-16 text-[12px] ${
            isDragOver ? "text-blue-400" : "text-gray-400"
          }`}>
            {isDoneCol ? "—" : t("kanban.empty")}
          </div>
        )}
      </div>
    </div>
  );
};

export const TaskKanban: React.FC<Props> = ({ tasks, onEdit, onStatusChange }) => {
  const { t } = useTranslation("tasks");

  const handleDrop = (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData(DRAG_KEY);
    if (!taskId) return;
    const task = tasks.find((tk) => tk._id === taskId);
    if (!task) return;
    if (task.status === targetStatus) return;
    if (task.status === "done") return;        // done tasks can't be moved
    if (targetStatus === "done") return;       // can't drag into done manually
    onStatusChange(taskId, targetStatus);
  };

  const tasksByStatus = (status: TaskStatus) => tasks.filter((t) => t.status === status);

  if (tasks.length === 0) {
    return (
      <div className="text-center py-16 text-sm text-gray-400">
        {t("list.empty")}
      </div>
    );
  }

  return (
    <div className="flex gap-3 w-full">
      {COLUMNS.map((col) => (
        <KanbanColumn
          key={col.status}
          status={col.status}
          labelKey={col.labelKey}
          tasks={tasksByStatus(col.status)}
          onEdit={onEdit}
          onDrop={handleDrop}
        />
      ))}
    </div>
  );
};
